import React, { useContext, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { DocumentosContext } from 'contexts/DocumentosContext';
import Container from 'components/Container';
import Header from 'components/Header';
import Loader from 'components/Loader';
import Card from 'components/Card';
import styles from './styles';
import moment from 'moment';

const casas_decimais_quantidade = 6;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
};

const somarQuantidade = (arr = [], field = 'quantidade') =>
  arr?.reduce((total, item) => total + toNumber(item?.[field]), 0) || 0;

const normalizarDecimal = (value) => {
  const number = toNumber(value);

  const factor = 10 ** casas_decimais_quantidade;
  return Math.round((number + Number.EPSILON) * factor) / factor;
};

const formatarQuantidade = (value) => {
  const number = Number(value);

  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Number.isNaN(number)) {
    return value;
  }

  return normalizarDecimal(number)?.toLocaleString('pt-br', {
    maximumFractionDigits: casas_decimais_quantidade,
  });
};

const Resumo = () => {
  const { id } = useParams();
  const [itens, setItens] = useState([]);
  const { getDocumento, getLoading, documento } = useContext(DocumentosContext);

  useEffect(() => {
    if (Boolean(id)) {
      getDocumento(id);
    }
  }, []);

  useEffect(() => {
    if (Boolean(id) && Boolean(documento)) {
      renderValues();
    }
  }, [documento]);

  const getVinculos = (natureza_operacao_id) => {
    switch (natureza_operacao_id) {
      case 19:
        return { natureza_retorno: 21 };
      case 22:
        return { natureza_retorno: 24 };
      case 37:
        return {
          natureza_venda: 44,
          natureza_retorno: 45,
          natureza_consignacao: 52,
          natureza_garantia: 54,
          natureza_bonificacao: 79,
        };
      case 39:
        return { natureza_retorno: 40, natureza_utilizacao: 11 };
      case 42:
        return { natureza_retorno: 43 };
      case 48:
        return { natureza_retorno: 49 };
      case 52:
        return { natureza_retorno: 45 };
      case 56:
        return { natureza_retorno: 57 };
      case 62:
        return { natureza_retorno: 63 };
      case 72:
        return { natureza_retorno: 87 };
      case 78:
        return {
          natureza_venda: 44,
          natureza_retorno: 45,
          natureza_consignacao: 52,
          natureza_garantia: 54,
          natureza_bonificacao: 79,
        };
      case 89:
        return { natureza_retorno: 90 };
      case 91:
        return { natureza_retorno: 92 };
      case 93:
        return { natureza_retorno: 94 };
      case 96:
        return { natureza_retorno: 97 };
      case 98:
        return { natureza_retorno: 99 };
      case 117:
        return { natureza_retorno: 116 };
      case 119:
        return { natureza_retorno: 118 };
      case 122:
        return { natureza_retorno: 121 };

      default:
        return null;
    }
  };

  const renderValues = () => {
    const itens = documento?.DocumentoItems?.map((di) => {
      const naturezas = getVinculos(documento?.natureza_operacao_id);
      const quantidade = toNumber(di?.quantidade);
      const quantidade_retornada = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_retorno
        )
      );
      const quantidade_vendida = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_venda
        )
      );
      const quantidade_consignada = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_consignacao
        )
      );
      const quantidade_garantia = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_garantia
        )
      );
      const quantidade_bonificada = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_bonificacao
        )
      );
      const quantidade_perdida = somarQuantidade(
        di?.Vinculos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_perda
        )
      );
      const quantidade_utilizada = somarQuantidade(
        di?.Destinos?.filter(
          (f) => f?.natureza_operacao_id === naturezas?.natureza_utilizacao
        )
      );
      const saldo = normalizarDecimal(
        quantidade -
          quantidade_garantia -
          quantidade_consignada -
          quantidade_retornada -
          quantidade_bonificada -
          quantidade_vendida -
          quantidade_perdida -
          quantidade_utilizada
      );
      return {
        id: di?.id,
        preco: di?.preco || 0,
        Vinculos: di?.Vinculos,
        referencia: di?.referencia,
        descricao: di?.descricao || '',
        saldo,
        quantidade: normalizarDecimal(quantidade),
        quantidade_retornada: normalizarDecimal(quantidade_retornada),
        quantidade_vendida: normalizarDecimal(quantidade_vendida),
        quantidade_consignada: normalizarDecimal(quantidade_consignada),
        quantidade_garantia: normalizarDecimal(quantidade_garantia),
        quantidade_bonificada: normalizarDecimal(quantidade_bonificada),
        quantidade_perdida: normalizarDecimal(quantidade_perdida),
        quantidade_utilizada: normalizarDecimal(quantidade_utilizada),
      };
    });
    setItens(itens);
  };

  const Line = ({ value, label }) => (
    <Box display="flex" alignItems="center">
      <Typography sx={styles?.lineLabel} variant="subtitle2">
        {label}:
      </Typography>
      <Typography sx={styles?.lineValue} variant="body2">
        {value}
      </Typography>
    </Box>
  );

  if (getLoading) {
    return <Loader />;
  }

  return (
    <Container>
      <Header titulo="Resumo da Remessa" />
      <Card>
        {itens?.map((i) => (
          <Accordion key={i?.id?.toString()}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={styles?.container}>
                <Typography
                  variant="h6"
                  sx={styles?.title}
                  color={i?.saldo <= 0 && 'red'}
                >
                  {i?.descricao}
                </Typography>
                <Line value={i?.referencia} label="Referência" />
                <Line value={formatarQuantidade(i?.quantidade)} label="Total" />
                <Line
                  value={formatarQuantidade(i?.quantidade_vendida)}
                  label="Vendas"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_retornada)}
                  label="Retornos / Devoluções"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_bonificada)}
                  label="Bonificações"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_consignada)}
                  label="Consignações"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_garantia)}
                  label="Garantias"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_perdida)}
                  label="Perdas"
                />
                <Line
                  value={formatarQuantidade(i?.quantidade_utilizada)}
                  label="Utilizadas"
                />
                <Line value={formatarQuantidade(i?.saldo)} label="Saldo" />
                <Line
                  value={(i?.preco || 0)?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  label="Preço Unitário"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {i?.Vinculos?.map((v, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx?.toString()}>
                    <Card>
                      <Box sx={styles?.title}>
                        <Typography
                          fontSize={18}
                          variant="body1"
                          fontWeight="bold"
                        >
                          {v?.especie}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          {v?.natureza}
                        </Typography>
                      </Box>
                      <Line value={v?.cliente} label="Entidade" />
                      <Line value={v?.documento} label="Nº do Documento" />
                      <Line
                        value={
                          Boolean(v?.dtemissao) &&
                          moment(v?.dtemissao).isValid() &&
                          moment(v?.dtemissao).format('DD/MM/YYYY [às] HH:mm')
                        }
                        label="Data de Emissão"
                      />
                      <Line
                        value={formatarQuantidade(v?.quantidade)}
                        label="Quantidade"
                      />
                      <Line
                        value={(v?.preco || 0)?.toLocaleString('pt-br', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                        label="Preço Unitário"
                      />
                      <Line
                        value={(
                          (v?.preco || 0) * (v?.quantidade || 0)
                        )?.toLocaleString('pt-br', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                        label="Preço Total"
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Card>
    </Container>
  );
};

export default Resumo;
